import {
  Box,
  Icon,
  Paper,
  Stack,
  Typography,
  alpha,
  buttonBaseClasses
} from "@mui/material";
import React from "react";
import InteractiveContainer from "../InteractiveElement";
import switchBaseClasses from "@mui/material/internal/switchBaseClasses";
import { MISC_COLORS } from "@/themes/themeVariables";
import accessibilityUtils from "@/utils/accessibility";
import { MFToggle } from "../MFToggle";

interface SwitchCardProps {
  size: "small" | "medium" | "large";
  isSelected?: boolean;
  disabled?: boolean;
  showSwitch?: boolean;
  icon?: React.ReactNode;
  title: string;
  caption: string;
  onClick?: () => void;
  noOuterStyle?: boolean;
  postComponent?: React.ReactNode;
  postTitleComponent?: React.ReactNode;
  clickId: string;
}

export const SwitchCard = ({
  size,
  isSelected,
  disabled = false,
  showSwitch = false,
  icon,
  title,
  caption,
  onClick,
  noOuterStyle = false,
  postComponent,
  clickId,
  postTitleComponent
}: SwitchCardProps): React.ReactNode => {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex"
      }}
      component={Paper}
      elevation={0}
      borderRadius={4}
    >
      <InteractiveContainer
        selected={isSelected}
        outlined={false}
        disabled={disabled}
        color="secondary"
        sx={{
          borderRadius: 4,
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          ...(size === "large"
            ? {
                flexDirection: "column",
                opacity: 1,
                gap: postComponent ? 1 : 0,
                padding: 4,
                paddingBottom: postComponent ? 4 : 9
              }
            : {}),
          "&:focus-visible": showSwitch
            ? {}
            : {
                outline: `3px solid`,
                outlineColor: MISC_COLORS.FOCUS_COLOR,
                outlineOffset: 4
              },
          ...(postComponent || postTitleComponent
            ? {
                userSelect: "auto",
                pointerEvents: "auto"
              }
            : {}),
          ...(disabled
            ? {
                backgroundColor: (theme) =>
                  theme.palette.action.disabledBackground
              }
            : {
                backgroundColor: (theme) =>
                  isSelected
                    ? theme.palette.background.elevation1
                    : theme.palette.action.selected
              }),
          "&::after": {
            display: "none"
          }
        }}
        role="checkbox"
        tabIndex={showSwitch ? -1 : 0}
        onClick={() => {
          if (disabled) return;
          if (onClick) {
            onClick();
          }
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (disabled || showSwitch) return;
          accessibilityUtils.handleOnKeyDown(onClick)(e);
        }}
      >
        <Stack
          py={
            noOuterStyle ? 0 : size === "large" ? 4 : size === "medium" ? 4 : 2
          }
          px={noOuterStyle ? 0 : size === "large" ? 4 : 3}
          direction={"row"}
          sx={{
            flexGrow: 1,
            minHeight: size === "large" ? 256 : "auto"
          }}
          gap={1}
          id={`switch-${clickId}`}
          data-selected={isSelected}
        >
          <Stack
            flexGrow={1}
            direction={size === "large" ? "column" : "row"}
            rowGap={1}
            columnGap={1.5}
            alignItems={size === "large" ? "start" : "center"}
            justifyContent={size === "large" ? "center" : "flex-start"}
          >
            {icon ? (
              <Icon
                sx={{
                  height: 48,
                  width: 48,
                  color: "inherit"
                }}
              >
                {icon}
              </Icon>
            ) : null}

            <Box
              flexShrink={0}
              sx={{
                maxWidth: `calc(100% - 56px)`
              }}
            >
              {postTitleComponent ? (
                <Stack direction={"row"} gap={1} alignItems={"center"}>
                  <Typography
                    variant={size === "large" ? "h5" : "h6"}
                    color={"inherit"}
                  >
                    {title}
                  </Typography>
                  {postTitleComponent}
                </Stack>
              ) : (
                <Typography
                  variant={size === "large" ? "h5" : "h6"}
                  color={"inherit"}
                >
                  {title}
                </Typography>
              )}

              <Typography
                variant="caption"
                sx={{ paddingTop: size === "small" ? "2px" : 1 }}
                color={disabled ? "text.disabled" : "text.secondary"}
              >
                {caption}
              </Typography>
            </Box>
          </Stack>
          {showSwitch ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center"
              }}
            >
              <MFToggle
                inputProps={{
                  "aria-label": `toggle ${title}`,
                  tabIndex: 0
                }}
                checked={isSelected}
                disabled={disabled}
                color="secondary"
                tabIndex={0}
                sx={{
                  [`& .${switchBaseClasses.root}.${buttonBaseClasses.focusVisible}`]:
                    {
                      backgroundColor: alpha(MISC_COLORS.FOCUS_COLOR, 0.3)
                    }
                }}
              />
            </Box>
          ) : null}
        </Stack>
        {postComponent ? postComponent : null}
      </InteractiveContainer>
    </Box>
  );
};
